import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { AuthContext } from '../../../components/AuthContext';
import { FaDownload, FaEye, FaCalendarAlt, FaUser, FaRupeeSign, FaFileExcel, FaChartBar, FaUpload, FaTruck, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import './reports.css';

const ChoiceEntryReports = () => {
  const { user } = useContext(AuthContext);
  const [choiceEntries, setChoiceEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Set default date range to current month
  const getCurrentMonthRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const startDate = new Date(year, month, 1); 
    const endDate = new Date(year, month + 1, 0);

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };
  
  const [dateFilter, setDateFilter] = useState(getCurrentMonthRange());
  const [salesPersonFilter, setSalesPersonFilter] = useState('');
  const [salesPersons, setSalesPersons] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [statistics, setStatistics] = useState({});
  const [viewMode, setViewMode] = useState('table'); // 'table', 'stats'
  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [salesmanWiseStats, setSalesmanWiseStats] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchChoiceEntries();
    fetchSalesPersons();
  }, []);

  const fetchSalesPersons = async () => {
    try {
      // Try multiple paths to get company ID
      const companyId = user?.company?._id || 
                       user?.user?.company?._id || 
                       user?.user?.company ||
                       user?.company;
      
      console.log('User object:', user);
      console.log('Fetching sales persons for company ID:', companyId);
      
      if (!companyId) {
        console.error('No company ID found in user context');
        console.log('Available user paths:', {
          'user?.company?._id': user?.company?._id,
          'user?.user?.company?._id': user?.user?.company?._id,
          'user?.user?.company': user?.user?.company,
          'user?.company': user?.company
        });
        return;
      }
      
      const response = await axios.get(`${API_BASE}/employees/company2/${companyId}`);
      console.log('Sales persons response:', response.data);
      setSalesPersons(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching sales persons:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };

  const fetchChoiceEntries = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (dateFilter.startDate) params.append('startDate', dateFilter.startDate);
      if (dateFilter.endDate) params.append('endDate', dateFilter.endDate);
      if (salesPersonFilter) params.append('salesPerson', salesPersonFilter);
      
      // Use the same company ID logic as in fetchSalesPersons
      const companyId = user?.company?._id || 
                       user?.user?.company?._id || 
                       user?.user?.company ||
                       user?.company;
      
      if (companyId) params.append('companyId', companyId);

      const response = await axios.get(`${API_BASE}/reports/choice-entries?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setChoiceEntries(response.data.choiceEntries || []);
      setTotalAmount(response.data.totalAmount || 0);
      calculateStatistics(response.data.choiceEntries || []);
      calculateSalesmanWiseStats(response.data.choiceEntries || []);
    } catch (error) {
      console.error('Error fetching choice entries:', error);
      toast.error('Failed to fetch choice entry reports');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    setCurrentPage(1); // Reset to first page when filtering
    fetchChoiceEntries();
  };

  const calculateStatistics = (entries) => {
    // Helper function to calculate amount from items
    const getEntryAmount = (entry) => {
      if (!entry.items || !Array.isArray(entry.items)) return 0;
      return entry.items.reduce((sum, item) => sum + (item.price || 0), 0);
    };

    const stats = {
      totalEntries: entries.length,
      avgAmount: entries.length > 0 ? entries.reduce((sum, entry) => sum + getEntryAmount(entry), 0) / entries.length : 0,
      maxAmount: entries.length > 0 ? Math.max(...entries.map(entry => getEntryAmount(entry))) : 0,
      minAmount: entries.length > 0 ? Math.min(...entries.map(entry => getEntryAmount(entry))) : 0,
      statusBreakdown: {},
      monthlyBreakdown: {},
      dailyAverage: 0
    };

    // Status breakdown
    entries.forEach(entry => {
      const status = entry.status || 'Pending';
      stats.statusBreakdown[status] = (stats.statusBreakdown[status] || 0) + 1;
    });

    // Monthly breakdown
    entries.forEach(entry => {
      const month = new Date(entry.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short' });
      stats.monthlyBreakdown[month] = (stats.monthlyBreakdown[month] || 0) + getEntryAmount(entry);
    });

    // Calculate daily average
    if (dateFilter.startDate && dateFilter.endDate) {
      const startDate = new Date(dateFilter.startDate);
      const endDate = new Date(dateFilter.endDate);
      const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      stats.dailyAverage = entries.length / daysDiff;
    } else if (entries.length > 0) {
      // Calculate based on the date range of entries if no filter is applied
      const dates = entries.map(entry => new Date(entry.createdAt));
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      const daysDiff = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) + 1;
      stats.dailyAverage = entries.length / daysDiff;
    }

    setStatistics(stats);
  };

  const calculateSalesmanWiseStats = (entries) => {
    // Helper function to calculate amount from items
    const getEntryAmount = (entry) => {
      if (!entry.items || !Array.isArray(entry.items)) return 0;
      return entry.items.reduce((sum, item) => sum + (item.price || 0), 0);
    };

    const salesmanStats = {};
    
    entries.forEach(entry => {
      const salesmanId = entry.createdBy?._id;
      const salesmanName = entry.createdBy?.name || 'Unknown';
      
      if (!salesmanStats[salesmanId]) {
        salesmanStats[salesmanId] = {
          name: salesmanName,
          totalEntries: 0,
          totalAmount: 0,
          avgAmount: 0,
          entries: []
        };
      }
      
      salesmanStats[salesmanId].totalEntries++;
      salesmanStats[salesmanId].totalAmount += getEntryAmount(entry);
      salesmanStats[salesmanId].entries.push(entry);
    });

    // Calculate averages
    Object.keys(salesmanStats).forEach(key => {
      const stats = salesmanStats[key];
      stats.avgAmount = stats.totalEntries > 0 ? stats.totalAmount / stats.totalEntries : 0;
    });

    setSalesmanWiseStats(Object.values(salesmanStats));
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (dateFilter.startDate) params.append('startDate', dateFilter.startDate);
      if (dateFilter.endDate) params.append('endDate', dateFilter.endDate);
      if (salesPersonFilter) params.append('salesPerson', salesPersonFilter);
      
      const companyId = user?.company?._id || 
                       user?.user?.company?._id || 
                       user?.user?.company ||
                       user?.company;
      if (companyId) params.append('companyId', companyId);

      const response = await axios.get(`${API_BASE}/reports/choice-entries/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `choice-entries-report-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV export completed successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

  const handleExcelExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (dateFilter.startDate) params.append('startDate', dateFilter.startDate);
      if (dateFilter.endDate) params.append('endDate', dateFilter.endDate);
      if (salesPersonFilter) params.append('salesPerson', salesPersonFilter);
      
      const companyId = user?.company?._id || 
                       user?.user?.company?._id || 
                       user?.user?.company ||
                       user?.company;
      if (companyId) params.append('companyId', companyId);

      const response = await axios.get(`${API_BASE}/reports/choice-entries/export-excel?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `choice-entries-detailed-report-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel file exported successfully!');
    } catch (error) {
      console.error('Error exporting Excel data:', error);
      toast.error('Failed to export Excel data');
    }
  };

  const handleSalesmanWiseExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (dateFilter.startDate) params.append('startDate', dateFilter.startDate);
      if (dateFilter.endDate) params.append('endDate', dateFilter.endDate);
      if (salesPersonFilter) params.append('salesPerson', salesPersonFilter);
      
      const companyId = user?.company?._id || 
                       user?.user?.company?._id || 
                       user?.user?.company ||
                       user?.company;
      if (companyId) params.append('companyId', companyId);

      const response = await axios.get(`${API_BASE}/reports/choice-entries/export-salesperson-wise?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `salesperson-wise-choice-entries-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Salesperson-wise report exported successfully!');
    } catch (error) {
      console.error('Error exporting salesperson-wise data:', error);
      toast.error('Failed to export salesperson-wise data');
    }
  };

  const handleSelectedExport = async () => {
    if (selectedEntries.size === 0) {
      toast.warning('Please select entries to export');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const selectedIds = Array.from(selectedEntries);
      
      const companyId = user?.company?._id || 
                       user?.user?.company?._id || 
                       user?.user?.company ||
                       user?.company;
      
      const response = await axios.post(`${API_BASE}/reports/choice-entries/export-selected`, {
        selectedIds,
        companyId: companyId
      }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `selected-choice-entries-detailed-reports-${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${selectedIds.length} comprehensive choice entry reports exported successfully!`);
    } catch (error) {
      console.error('Error exporting selected data:', error);
      toast.error('Failed to export selected entries');
    }
  };

  const handleExportAllSeparate = async () => {
    if (choiceEntries.length === 0) {
      toast.warning('No choice entries to export');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const allIds = choiceEntries.map(entry => entry._id);
      
      const companyId = user?.company?._id || 
                       user?.user?.company?._id || 
                       user?.user?.company ||
                       user?.company;
      
      const response = await axios.post(`${API_BASE}/reports/choice-entries/export-all-separate`, {
        allIds,
        companyId: companyId
      }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all-choice-entries-detailed-reports-${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${choiceEntries.length} comprehensive choice entry reports exported successfully!`);
    } catch (error) {
      console.error('Error exporting all data:', error);
      toast.error('Failed to export all entries');
    }
  };

  const handleSelectEntry = (entryId) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      newSelected.add(entryId);
    }
    setSelectedEntries(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedEntries.size === currentEntries.length) {
      // Remove current page entries from selection
      const newSelected = new Set(selectedEntries);
      currentEntries.forEach(entry => newSelected.delete(entry._id));
      setSelectedEntries(newSelected);
    } else {
      // Add current page entries to selection
      const newSelected = new Set(selectedEntries);
      currentEntries.forEach(entry => newSelected.add(entry._id));
      setSelectedEntries(newSelected);
    }
  };

  const handleViewEntry = (entry) => {
    console.log('Selected entry for viewing:', entry);
    console.log('Items in entry:', entry.items);
    setSelectedEntry(entry);
    setShowModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEntries = choiceEntries.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(choiceEntries.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">Choice Entry Reports</h2>
        <p className="text-gray-600">Advanced analytics and reporting for choice entries</p>
      </div>

      {/* View Mode Tabs */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-gray-200 p-1 rounded-lg w-fit">
          <button
            onClick={() => setViewMode('table')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'table' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <FaEye className="inline mr-2" />
            Table View
          </button>
          <button
            onClick={() => setViewMode('stats')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'stats' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <FaChartBar className="inline mr-2" />
            Statistics
          </button>
        </div>
      </div>

      {/* Enhanced Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Advanced Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={dateFilter.startDate}
              onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={dateFilter.endDate}
              onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sales Person</label>
            <Select
              value={salesPersons.find(p => p._id === salesPersonFilter) ? {
                value: salesPersonFilter,
                label: salesPersons.find(p => p._id === salesPersonFilter)?.name
              } : null}
              onChange={(selected) => setSalesPersonFilter(selected?.value || '')}
              options={[
                { value: '', label: 'All Sales Persons' },
                ...salesPersons.map(person => ({
                  value: person._id,
                  label: person.name
                }))
              ]}
              placeholder="Select Sales Person"
              isClearable
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
          <div className="flex items-end space-x-2">
            <button
              onClick={handleFilter}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center transition-colors shadow-md"
            >
              <FaEye className="mr-2" />
              Filter
            </button>
          </div>
        </div>

        {/* Export Buttons */}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center transition-colors text-sm"
          >
            <FaDownload className="mr-2" />
            CSV Export
          </button>
          <button
            onClick={handleExcelExport}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center transition-colors text-sm"
          >
            <FaFileExcel className="mr-2" />
            Excel Export
          </button>
          <button
            onClick={handleSalesmanWiseExport}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center transition-colors text-sm"
          >
            <FaUser className="mr-2" />
            Salesperson Stats
          </button>
          {selectedEntries.size > 0 && (
            <button
              onClick={handleSelectedExport}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center transition-colors text-sm"
              title="Export each selected choice entry as a comprehensive Excel file containing: Choice details (ID, date, sales person, customer info, status, total amount) + Complete items breakdown (Tally item names, codes, groups, descriptions, quantities, pricing, calculation types, application areas, remarks, and all dimensional details)"
            >
              <FaFileExcel className="mr-2" />
              Export Selected ({selectedEntries.size}) - Complete Details (Separate Files)
            </button>
          )}
          <button
            onClick={handleExportAllSeparate}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center transition-colors text-sm"
            title="Export each choice entry as a comprehensive Excel file containing: Choice details (ID, date, sales person, customer info, status, total amount) + Complete items breakdown (Tally item names, codes, groups, descriptions, quantities, pricing, calculation types, application areas, remarks, and all dimensional details)"
          >
            <FaFileExcel className="mr-2" />
            Export All - Complete Details (Separate Files)
          </button>
          {choiceEntries.length > itemsPerPage && (
            <div className="text-xs text-gray-500 bg-gray-100 px-3 py-2 rounded-lg">
              <span>Showing {currentEntries.length} of {choiceEntries.length} entries (Page {currentPage} of {totalPages})</span>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100">Total Entries</p>
              <p className="text-3xl font-bold">{choiceEntries.length}</p>
            </div>
            <FaCalendarAlt className="text-3xl text-blue-200" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100">Total Amount</p>
              <p className="text-3xl font-bold">{formatCurrency(totalAmount)}</p>
            </div>
            <FaRupeeSign className="text-3xl text-green-200" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100">Average per Entry</p>
              <p className="text-3xl font-bold">
                {formatCurrency(statistics.avgAmount || 0)}
              </p>
            </div>
            <FaUser className="text-3xl text-purple-200" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100">Daily Average</p>
              <p className="text-3xl font-bold">{statistics.dailyAverage?.toFixed(1) || '0'}</p>
            </div>
            <FaChartBar className="text-3xl text-orange-200" />
          </div>
        </div>
      </div>

      {/* Conditional View Rendering */}
      {viewMode === 'stats' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Status Breakdown */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Status Breakdown</h3>
            <div className="space-y-3">
              {Object.entries(statistics.statusBreakdown || {}).map(([status, count]) => (
                <div key={status} className="flex justify-between items-center">
                  <span className="capitalize">{status}</span>
                  <div className="flex items-center">
                    <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${(count / choiceEntries.length) * 100}%` }}
                      ></div>
                    </div>
                    <span className="font-semibold">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Salesperson Performance */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Salesperson Performance</h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {salesmanWiseStats.map((salesman, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{salesman.name}</p>
                    <p className="text-sm text-gray-600">{salesman.totalEntries} entries</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{formatCurrency(salesman.totalAmount)}</p>
                    <p className="text-sm text-gray-600">Avg: {formatCurrency(salesman.avgAmount)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Data Table */}
      {viewMode === 'table' && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-semibold">Choice Entries</h3>
            {choiceEntries.length > 0 && (
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {selectedEntries.size === currentEntries.length && currentEntries.every(entry => selectedEntries.has(entry._id)) 
                  ? 'Deselect All (Current Page)' 
                  : 'Select All (Current Page)'}
              </button>
            )}
          </div>
          
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading choice entries...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={currentEntries.length > 0 && currentEntries.every(entry => selectedEntries.has(entry._id))}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sales Person
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Interior/Guest
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentEntries.length > 0 ? (
                    currentEntries.map((entry, index) => (
                      <tr 
                        key={entry._id || index} 
                        className={`hover:bg-gray-50 transition-colors ${
                          selectedEntries.has(entry._id) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedEntries.has(entry._id)}
                            onChange={() => handleSelectEntry(entry._id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(entry.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                              <span className="text-blue-600 font-semibold text-xs">
                                {(entry.createdBy?.name || 'N/A').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            {entry.createdBy?.name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {entry.customerName || 
                           (entry.contactType === 'Guest' && entry.guest?.guestName) ||
                           (entry.contactType === 'Reference' && entry.reference?.name) ||
                           (entry.contactType === 'Customer' && entry.customer?.name) ||
                           (entry.contactType === 'Dealer' && entry.dealer?.businessName) ||
                           (entry.contactType === 'Guest' ? 'Guest' : 'N/A')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                            {entry.contactType || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(entry.items?.reduce((sum, item) => sum + (item.price || 0), 0) || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full shadow-sm ${
                              entry.status === 'Send for quotation'
                                ? 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-300' 
                                : entry.status === 'Send for billing'
                                ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300'
                                : entry.status === 'Cancelled'
                                ? 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-300'
                                : entry.status === 'Draft'
                                ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border border-gray-300'
                                : 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border border-yellow-300'
                            }`}>
                              <div className={`w-2 h-2 rounded-full mr-2 ${
                                entry.status === 'Send for quotation'
                                  ? 'bg-purple-500' 
                                  : entry.status === 'Send for billing'
                                  ? 'bg-green-500'
                                  : entry.status === 'Cancelled'
                                  ? 'bg-red-500'
                                  : entry.status === 'Draft'
                                  ? 'bg-gray-500'
                                  : 'bg-yellow-500'
                              }`}></div>
                              {entry.status || 'Pending'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleViewEntry(entry)}
                            className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                          >
                            <FaEye className="mr-1" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                        <FaCalendarAlt className="mx-auto text-4xl text-gray-300 mb-4" />
                        <p className="text-lg font-medium">No choice entries found</p>
                        <p className="text-sm">Try adjusting your filters or date range</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination Controls */}
          {choiceEntries.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Items per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-gray-700">
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, choiceEntries.length)} of {choiceEntries.length} entries
                  </span>
                </div>
                
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    <FaChevronLeft className="mr-1" />
                    Previous
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNumber;
                    if (totalPages <= 5) {
                      pageNumber = i + 1;
                    } else if (currentPage <= 3) {
                      pageNumber = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNumber = totalPages - 4 + i;
                    } else {
                      pageNumber = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`px-3 py-1 border border-gray-300 rounded text-sm ${
                          currentPage === pageNumber
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    Next
                    <FaChevronRight className="ml-1" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Entry Details Modal */}
      {showModal && selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-800">Choice Entry Details</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Entry Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-600 mb-1">Date Created</label>
                  <p className="text-lg font-semibold text-gray-800">{formatDate(selectedEntry.createdAt)}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-600 mb-1">Entry ID</label>
                  <p className="text-lg font-semibold text-gray-800">{selectedEntry._id || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-600 mb-1">Sales Person</label>
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-semibold text-xs">
                        {(selectedEntry.createdBy?.name || 'N/A').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-800">{selectedEntry.createdBy?.name || 'N/A'}</p>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full shadow-sm ${
                    selectedEntry.status === 'Send for quotation'
                      ? 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-300' 
                      : selectedEntry.status === 'Send for billing'
                      ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300'
                      : selectedEntry.status === 'Cancelled'
                      ? 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-300'
                      : selectedEntry.status === 'Draft'
                      ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border border-gray-300'
                      : 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border border-yellow-300'
                  }`}>
                    <div className={`w-2 h-2 rounded-full mr-2 ${
                      selectedEntry.status === 'Send for quotation'
                        ? 'bg-purple-500' 
                        : selectedEntry.status === 'Send for billing'
                        ? 'bg-green-500'
                        : selectedEntry.status === 'Cancelled'
                        ? 'bg-red-500'
                        : selectedEntry.status === 'Draft'
                        ? 'bg-gray-500'
                        : 'bg-yellow-500'
                    }`}></div>
                    {selectedEntry.status || 'Pending'}
                  </span>
                </div>
              </div>

              {/* Customer Information */}
              <div className="border-t pt-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Interior/Guest Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Interior/Guest Name</label>
                    <p className="text-lg font-semibold text-gray-800">
                      {selectedEntry.customerName || 
                       (selectedEntry.contactType === 'Guest' && selectedEntry.guest?.guestName) ||
                       (selectedEntry.contactType === 'Reference' && selectedEntry.reference?.name) ||
                       (selectedEntry.contactType === 'Customer' && selectedEntry.customer?.name) ||
                       (selectedEntry.contactType === 'Dealer' && selectedEntry.dealer?.businessName) ||
                       (selectedEntry.contactType === 'Guest' ? 'Guest' : 'N/A')}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Contact Type</label>
                    <p className="text-lg font-semibold text-gray-800">{selectedEntry.contactType || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Financial Information */}
              <div className="border-t pt-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Financial Details</h4>
                <div className="bg-green-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-600 mb-1">Total Amount</label>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedEntry.items?.reduce((sum, item) => sum + (item.price || 0), 0) || 0)}</p>
                </div>
              </div>

              {/* Items Information */}
              {selectedEntry.items && selectedEntry.items.length > 0 && (
                <div className="border-t pt-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Items Details ({selectedEntry.items.length} items)</h4>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {selectedEntry.items.map((item, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h5 className="font-semibold text-gray-800">
                              {item.tallyItem?.name || item.tallyItem?.itemName || item.name || item.itemName || `Item ${index + 1}`}
                            </h5>
                            {item.tallyItem?.code && (
                              <p className="text-sm font-medium text-blue-600 mt-1">Tally Code: {item.tallyItem.code}</p>
                            )}
                            {item.tallyItem?.description && (
                              <p className="text-sm text-gray-600 mt-1">{item.tallyItem.description}</p>
                            )}
                            {item.tallyItem?.group && (
                              <p className="text-sm text-indigo-600 mt-1">Tally Group: {item.tallyItem.group}</p>
                            )}
                            {item.applicationArea && (
                              <p className="text-sm text-blue-600 mt-1">Application: {item.applicationArea}</p>
                            )}
                            {item.remarks && (
                              <p className="text-sm text-gray-600 mt-1">Remarks: {item.remarks}</p>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <p className="font-semibold text-lg text-green-600">
                              {formatCurrency((item.price || 0) * (item.quantity || 0))}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Quantity: </span>
                            <span className="font-medium">{item.quantity || 0}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Price: </span>
                            <span className="font-medium">{formatCurrency(item.price || 0)}</span>
                          </div>
                          {item.calculationType && (
                            <div>
                              <span className="text-gray-600">Calc Type: </span>
                              <span className="font-medium">{item.calculationType}</span>
                            </div>
                          )}
                          {item.uom && (
                            <div>
                              <span className="text-gray-600">UOM: </span>
                              <span className="font-medium">{item.uom}</span>
                            </div>
                          )}
                          {item.tallyItem?.category && (
                            <div>
                              <span className="text-gray-600">Category: </span>
                              <span className="font-medium">{item.tallyItem.category}</span>
                            </div>
                          )}
                          {item.tallyItem?.group && (
                            <div>
                              <span className="text-gray-600">Group: </span>
                              <span className="font-medium">{item.tallyItem.group}</span>
                            </div>
                          )}
                          {item.calculationType === 'pcsPerBox' && item.boxQuantity > 0 && (
                            <>
                              <div>
                                <span className="text-gray-600">Box Qty: </span>
                                <span className="font-medium">{item.boxQuantity}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Pcs Qty: </span>
                                <span className="font-medium">{item.pcsQuantity}</span>
                              </div>
                            </>
                          )}
                          {item.areaValue > 0 && (
                            <div>
                              <span className="text-gray-600">Area: </span>
                              <span className="font-medium">{item.areaValue} {item.areaUnit}</span>
                            </div>
                          )}
                          {item.pricePerSqft > 0 && (
                            <div>
                              <span className="text-gray-600">Price/SqFt: </span>
                              <span className="font-medium">{formatCurrency(item.pricePerSqft)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 bg-green-50 p-3 rounded-lg border border-green-200">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-700">Total Items Amount:</span>
                      <span className="text-xl font-bold text-green-600">
                        {formatCurrency(selectedEntry.items.reduce((sum, item) => 
                          sum + ((item.price || 0) * (item.quantity || 0)), 0
                        ))}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {(!selectedEntry.items || selectedEntry.items.length === 0) && (
                <div className="border-t pt-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Items Details</h4>
                  <div className="bg-gray-50 p-6 rounded-lg text-center">
                    <p className="text-gray-500">No items found for this choice entry</p>
                    {selectedEntry.items && (
                      <p className="text-xs text-gray-400 mt-2">Items array exists but is empty (length: {selectedEntry.items.length})</p>
                    )}
                    {!selectedEntry.items && (
                      <p className="text-xs text-gray-400 mt-2">Items array is null or undefined</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleSelectedExport();
                  setSelectedEntries(new Set([selectedEntry._id]));
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center transition-colors"
                title="Export this choice entry as a comprehensive Excel file with complete choice details and full items breakdown including Tally information, pricing, calculations, and all specifications"
              >
                <FaFileExcel className="mr-2" />
                Export Complete Choice Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChoiceEntryReports;
